<template>
  <div class="caption-settings">
    <div class="font-bold text-text-neutral-extreme text-[16px] leading-[24px] mb-7" v-if="showHeader">{{ captionSettingsTitle }}</div>

    <div class="mb-6">
      <label class="block text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2" :for="`caption_font_size_${salt}`">{{ sizeLabel }}</label>
      <div class="flex flex-row items-center gap-3">
        <div class="flex flex-row rounded-[10px] overflow-hidden">
          <button :disabled="fontSize <= minFontSize" class="bg-[#282F35] h-9 w-10 px-2 py-1 flex justify-center items-center cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-decrease" @click="emit('step-font-size', -1)" aria-label="Decrease Font Size">
            <img width="24" height="24" src="@/assets/icons/minus.svg" alt="-" />
          </button>
          <button :disabled="fontSize >= maxFontSize" class="bg-[#282F35] h-9 w-10 px-2 py-1 flex justify-center items-center border-l-[3px] border-layer-0 cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-increase" @click="emit('step-font-size', 1)" aria-label="Increase Font Size">
            <img width="24" height="24" src="@/assets/icons/plus.svg" alt="+" />
          </button>
        </div>
        <input
            :id="`caption_font_size_${salt}`"
            v-model="fontSizeDraft"
            type="number"
            inputmode="numeric"
            :min="minFontSize"
            :max="maxFontSize"
            step="1"
            class="font-size-input border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] leading-[1.18rem] h-9 w-[64px] px-[10px] outline-none focus:border-[#6CBFFF]"
            data-testid="font-size-input"
            @change="commitFontSize"
            @keydown.enter="$event.target.blur()"
        />
      </div>
    </div>

    <div class="mb-6">
      <div class="text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2">{{ widthLabel }}</div>
      <div class="flex flex-row gap-2" role="radiogroup" :aria-label="widthLabel">
        <button
            v-for="option in widthOptions"
            :key="option.value"
            role="radio"
            :aria-checked="width === option.value"
            class="flex flex-col items-center justify-center gap-[5px] w-[70px] h-[60px] rounded-[10px] text-[14px] leading-[18px] font-medium text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
            :class="width === option.value ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :data-testid="`width-option-${option.value}`"
            @click="emit('width', option.value)"
        >
          <img :src="option.icon" alt="" class="w-[26px] h-[26px]" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <div class="mb-6">
      <div class="text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2">{{ alignmentLabel }}</div>
      <div class="flex flex-row gap-2" role="radiogroup" :aria-label="alignmentLabel">
        <button
            v-for="option in alignOptions"
            :key="option.value"
            role="radio"
            :aria-checked="align === option.value"
            class="flex flex-col items-center justify-center gap-[5px] w-[70px] h-[60px] rounded-[10px] text-[14px] leading-[18px] font-medium text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
            :class="align === option.value ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :data-testid="`align-option-${option.value}`"
            @click="emit('align', option.value)"
        >
          <img :src="option.icon" alt="" class="w-[26px] h-[26px]" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <div class="mb-6">
      <div class="text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2">{{ fontLabel }}</div>
      <div class="flex flex-row gap-2" role="radiogroup" :aria-label="fontLabel">
        <button
            v-for="option in fontOptions"
            :key="option.value"
            role="radio"
            :aria-checked="font === option.value"
            class="flex items-center justify-center h-[46px] px-4 rounded-[10px] text-[14px] leading-[20px] text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
            :class="font === option.value ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :style="{ fontFamily: option.css }"
            :data-testid="`font-option-${option.value}`"
            @click="emit('font', option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div class="mb-6">
      <div class="text-text-neutral-high text-[14px] leading-[20px] font-medium mb-2">{{ themeLabel }}</div>
      <div class="flex flex-row gap-2" role="radiogroup" :aria-label="themeLabel">
        <button
            v-for="option in themeOptions"
            :key="option.value"
            role="radio"
            :aria-checked="theme === option.value"
            class="flex flex-col items-center justify-center gap-[5px] w-[70px] h-[60px] rounded-[10px] text-[14px] leading-[18px] font-medium text-text-neutral-high cursor-pointer hover:bg-button-secondary-mono-active"
            :class="theme === option.value ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :data-testid="`theme-option-${option.value}`"
            @click="emit('theme', option.value)"
        >
          <!-- Moon / sun icons carried over from the removed Presentation view -->
          <svg class="w-[26px] h-[26px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" :d="option.iconPath" fill="currentColor"/>
          </svg>
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue';
import { useCaptionSettingsOptions, useFontSizeDraft } from '@/composables/useCaptionSettings';

const props = defineProps({
  fontSize: {
    type: Number,
    required: true,
  },
  minFontSize: Number,
  maxFontSize: Number,
  align: {
    type: String,
    required: false,
    default: 'left',
  },
  width: {
    type: String,
    required: false,
    default: 'wide',
  },
  theme: {
    type: String,
    required: false,
    default: 'dark',
  },
  // Style key from Constants.CAPTION_FONTS, not a font name.
  font: {
    type: String,
    required: false,
    default: 'sans',
  },
  showHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
  salt: {
    type: String,
    required: false,
    default: 'popup',
  },
})

const emit = defineEmits(['step-font-size', 'set-font-size', 'align', 'width', 'font', 'theme'])

const {
  captionSettingsTitle,
  sizeLabel,
  alignmentLabel,
  widthLabel,
  fontLabel,
  themeLabel,
  widthOptions,
  themeOptions,
  fontOptions,
  alignOptions,
} = useCaptionSettingsOptions()

const { fontSizeDraft, commitFontSize } = useFontSizeDraft(props, emit)
</script>

<style scoped>
.font-size-input {
  -moz-appearance: textfield;
  appearance: textfield;
  text-align: center;
}

.font-size-input::-webkit-outer-spin-button,
.font-size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
